<template>
  <div class="w-full lg:w-[420px] flex flex-col gap-5 kft-product__checkout">
    <EventBuyBox
      v-if="shouldDisplayEventBuybox || isKitsAvailable"
      :cart-key="cartKey"
      :is-embedded="isEmbedded"
      :text-color="textColor"
      :background-color="backgroundColor"
      :is-kits-page="isKitsPage"
      :enable-giftcard="false"
      @dates-loaded="emit('dates-loaded')"
      @add-item-to-cart="emit('add-item-to-cart')"
      @toggle-date-picker="emit('toggle-date-picker')"
    />

    <EventGiftcardBuyBoxCard
      v-if="shouldEnableGiftcard"
      :text-color="textColor"
      :enable-embedded="isEmbedded"
      :background-color="backgroundColor"
      display-price
      :type="eventGetters.getIsTeamEvent(eventData) ? 'PRIVATE' : 'PUBLIC'"
      :should-display-buy-box="shouldDisplayEventBuybox"
      :is-special-giftcard-event="isSpecialGiftcardEvent"
      @add-item-to-cart="emit('add-item-to-cart')"
    />

    <LazyTeameventRequestCard
      v-if="shouldDisplayTeamEventRequestCard"
      :price="
        !eventGetters.getNextDateAvailable(eventData) &&
        formatPriceAmount(
          Number(eventGetters.getDefaultPriceAmount(eventData) || 0),
          locale
        )
      "
    />
  </div>
</template>

<script lang="ts" setup>
import type {
  EventCalendarDayMap,
  EventDescription,
} from '~/composables/types/event'
import { useIsSubdomain } from '~/composables/useSubdomain'

const { locale } = useI18n()

const eventBuyBoxVisibleElements = {
  giftcard: true,
  ...(inject('eventBuyBoxVisibleElements') as {
    giftcard?: boolean
  }),
}

const props = defineProps<{
  cartKey: string
  textColor?: string
  backgroundColor?: string
  isEmbedded: boolean
}>()

const emit = defineEmits<{
  (e: 'dates-loaded'): void
  (e: 'toggle-date-picker'): void
  (e: 'add-item-to-cart'): void
}>()

const eventData = inject('eventData') as EventDescription
const isSpecialGiftcardEvent = inject('isSpecialGiftcardEvent') as boolean
const router = useRouter()
const isKitsPage = computed(
  () =>
    router.currentRoute.value.name?.toString().split('___')[0] ===
      'k-boxPermalink' ||
    (props.isEmbedded && eventGetters.isBoxEvent(eventData))
)

const isSubdomain = useIsSubdomain()
const calendarDates = ref<EventCalendarDayMap | Record<any, any> | undefined>(
  undefined
)

const areThereDatesAvailable = computed(
  () => Object.values(calendarDates.value || {}).length > 0
)

const shouldEnableGiftcard = computed(
  () =>
    eventBuyBoxVisibleElements.giftcard &&
    !eventGetters.getIsTeamEvent(eventData) &&
    (!isSubdomain.value ||
      supplierGetters.getHasGiftcardEmbedded(
        eventGetters.getSupplier(eventData)
      ))
)

const shouldDisplayEventBuybox = computed(
  () => !isSpecialGiftcardEvent && areThereDatesAvailable.value
)

const isKitsAvailable = computed(
  () =>
    isKitsPage &&
    Object.values(calendarDates.value ?? {}).length > 0 &&
    calendarDates.value?.[0]?.tickets_left > 0
)

const shouldDisplayTeamEventRequestCard = computed(
  () =>
    !props.isEmbedded &&
    !isSpecialGiftcardEvent &&
    eventGetters.getAttendanceMode(eventData) !== 'NATIONWIDE'
)

const config = useRuntimeConfig()
const { apiBaseURL } = config.public

onMounted(async () => {
  await nextTick()
  if (isSpecialGiftcardEvent) return
  calendarDates.value = await $fetch(
    `/v1/store/events/${eventGetters.getId(eventData)}/calendar`,
    {
      method: 'GET',
      baseURL: apiBaseURL,
    }
  )
  emit('dates-loaded')
})
</script>
